
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { formatText } from "./utils/textFormatting";
import { ProductFormValues } from "./types";
import { ProductFormFields } from "./ProductFormFields";

interface EditProductFormProps {
  productId: string;
  initialValues: ProductFormValues;
  onSuccess: () => void;
  onCancel: () => void;
}

export const EditProductForm = ({ 
  productId, 
  initialValues, 
  onSuccess, 
  onCancel 
}: EditProductFormProps) => {
  const { toast } = useToast();
  const form = useForm<ProductFormValues>({
    defaultValues: initialValues,
  });

  const onSubmit = async (data: ProductFormValues) => {
    try {
      const { error } = await supabase
        .from("products")
        .update({
          name: data.name,
          description: data.description,
          features: data.features,
          link: data.link,
          logo: data.logo,
          product_screen: data.product_screen,
          visible: data.visible,
        })
        .eq("id", productId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Product updated successfully",
      });
      
      onSuccess();
    } catch (error) {
      console.error("Error updating product:", error);
      toast({
        title: "Error",
        description: "Failed to update product",
        variant: "destructive",
      });
    }
  };

  const handleFormat = (format: string, fieldName: 'features') => {
    const textarea = document.querySelector(`textarea[name="${fieldName}"]`) as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    
    const formattedText = formatText(format, text, start, end);
    if (formattedText) {
      form.setValue(fieldName, formattedText);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <ProductFormFields form={form} handleFormat={handleFormat} />
        <Button type="submit" className="w-full">Update Product</Button>
      </form>
    </Form>
  );
};
